import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { commitDraft } from '@4cm/brain/draftCommit';
import { evaluateCoachFocus, type CoachFocusReport } from '@4cm/core/coachFocus';
import {
  COACHING_STYLES,
  COACHING_STYLE_META,
  inferCoachingStyle,
  type CoachingStyle,
} from '@4cm/core/coachingStyle';
import { evaluateStyleFit, suggestStyleAdjustments } from '@4cm/core/coachStyleGuard';
import {
  GRASSROOTS_PSYCH_SOCIAL_INTENTS,
  GRASSROOTS_PSYCH_SOCIAL_INTENT_META,
  type GrassrootsPsychSocialIntent,
} from '@4cm/core/psychSocialIntent';
import { tierForCoachLevel } from '@4cm/core/phaseOfPlay';
import { findTechnicalDrillsMissingDecision } from '@4cm/core/sessionPlannerPrompt';
import { Brand } from '../components/Brand';
import { BrainOrb, BrainPrompt, Button, Card, GlassPill } from '../components/primitives';
import { DraftCard } from '../components/DraftCard';
import { SessionCanvas } from '../components/sessionCanvas';
import { useAuth } from '../contexts/AuthContext';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useVoiceToText } from '../lib/voiceInput';
import { useBrainAutoSpeak } from '../lib/useBrainAutoSpeak';
import { useBrain } from '../lib/brain';
import type { BrainTurn } from '../lib/brain';
import { useCalibration } from '../contexts/CalibrationContext';
import { isPlaceholderSupabase } from '../lib/supabase';
import { saveLocalDraftSession } from '../lib/localTeam';
import './SessionNew.css';

const FIRST_PROMPT =
  "What do you want tonight's session to focus on? Give me as much or as little as you've got — phase, intensity, any constraints, the players you're worried about.";

export function SessionNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { team } = useActiveTeam();
  const { coachLevel, level } = useCalibration();
  const tier = tierForCoachLevel(coachLevel);
  // Canvas-first toggle (Phase C). Calibration drives the default —
  // academy → canvas-first; novice → Brain only (the toggle is
  // hidden entirely at novice so the surface stays simple). Coach can
  // flip via the "Switch to Brain" pill on the canvas header.
  const [canvasMode, setCanvasMode] = useState(level === 'academy');
  // Re-default whenever calibration changes (e.g. coach upgrades level
  // mid-session). Has no effect at novice because we hide the toggle.
  useEffect(() => {
    setCanvasMode(level === 'academy');
  }, [level]);
  // Brain context — 'session' kind so Brain knows the coach is planning
  // a session; team id anchors tool fetches against the right roster.
  const brain = useBrain({
    teamId: team?.id ?? null,
    screen: { kind: 'session', teamId: team?.id ?? null, note: 'Planning a new session' },
  });
  const [intro] = useState<BrainTurn>({ id: 'intro', role: 'assistant', text: FIRST_PROMPT });
  const [committing, setCommitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusReport, setFocusReport] = useState<CoachFocusReport | null>(null);
  const [focusChecking, setFocusChecking] = useState(false);

  // Optional structured context the coach can set BEFORE typing/speaking
  // to bias Brain's draft: coaching style + grassroots psych-social
  // intent. Both are optional; selected values are inlined into the
  // message Brain receives so the existing tool-loop picks them up
  // without any prompt changes.
  const [style, setStyle] = useState<CoachingStyle | null>(null);
  const [intent, setIntent] = useState<GrassrootsPsychSocialIntent | null>(null);

  // Decision-making validator (Ben item #8) — when a session draft is
  // ready to save, surface any TECHNICAL drills that lack a
  // decision_under_execution. Coach can save anyway (override) or
  // dismiss to let the AI re-draft from a follow-up message.
  const [decisionWarning, setDecisionWarning] = useState<{
    turnId: string;
    indices: number[];
  } | null>(null);

  // Technique-floor warning (Ben item #5) — when grassroots coach picks
  // possession_based, surface verdict + adjustments BEFORE generation.
  // We don't have rating telemetry yet, so meanTechRating is null →
  // verdict defaults to amber for possession_based at grassroots.
  const styleFit = useMemo(() => {
    if (!style) return null;
    const verdict = evaluateStyleFit({ style, tier, meanTechRating: null });
    if (verdict === 'green') return null;
    return { verdict, adjustments: suggestStyleAdjustments(style, verdict) };
  }, [style, tier]);

  // Compose a human-readable prefix from the structured pickers. Brain
  // sees this in the FIRST coach message so its draft respects both
  // axes (focus from free-text + style/intent from chips).
  const composeContext = useCallback((coachText: string): string => {
    const parts: string[] = [];
    if (style) parts.push(`Style: ${COACHING_STYLE_META[style].label}`);
    if (intent) parts.push(`Psych-social intent: ${GRASSROOTS_PSYCH_SOCIAL_INTENT_META[intent].label}`);
    if (parts.length === 0) return coachText;
    return `${parts.join(' · ')}\n\n${coachText}`;
  }, [style, intent]);

  /**
   * Focus-guidance pre-flight on the FIRST coach message — the discipline-
   * imposer side of the Brain. If recent sessions show high theme variance
   * (>3 distinct phases in 4 sessions, or this is a 5th fresh theme), surface
   * the AI's pushback BEFORE asking Brain to draft. Coach can revise (re-type
   * a different focus) or click "Continue anyway" to bypass and proceed.
   *
   * Best-effort only: if the analytics fail, fall through to normal Brain
   * send rather than blocking the coach.
   */
  const sendWithFocusCheck = useCallback(async (text: string) => {
    // Plain-English style classifier — if the coach hasn't picked a chip
    // and we can infer one from their phrase, surface the inference as a
    // pre-fill (the chip auto-selects but the coach can clear it).
    if (!style) {
      const inferred = inferCoachingStyle(text);
      if (inferred) setStyle(inferred);
    }
    const composed = composeContext(text);

    // Already past the first turn (or the offline placeholder backend has no
    // sessions to analyse) — skip the pre-flight, send straight through.
    if (brain.turns.length > 0 || !user || isPlaceholderSupabase()) {
      void brain.send(composed);
      return;
    }
    setFocusChecking(true);
    try {
      const report = await evaluateCoachFocus({ coachId: user.id, primaryFocus: text });
      if (report.shouldWarn && report.message) {
        setFocusReport({ ...report, proposedSubThemes: [...report.proposedSubThemes, composed] });
        return; // wait for coach to revise or click "continue anyway"
      }
      void brain.send(composed);
    } catch (err) {
      // Best-effort — don't block on analytics failure.
      // eslint-disable-next-line no-console
      console.warn('[focus-guidance] check failed', err);
      void brain.send(composed);
    } finally {
      setFocusChecking(false);
    }
  }, [brain, user, style, composeContext]);

  const continueAnyway = useCallback(() => {
    if (!focusReport) return;
    // The original primaryFocus is the last entry in proposedSubThemes (we
    // appended it above so we could recover it). Send through to Brain.
    const text = focusReport.proposedSubThemes[focusReport.proposedSubThemes.length - 1] ?? '';
    setFocusReport(null);
    if (text) void brain.send(text);
  }, [focusReport, brain]);

  // Auto-start voice listening hook (matches the Onboarding pattern)
  // Brain auto-speak — voice in → voice out (per the founder-sync rule).
  // Replay button is on BrainChatPanel; here we just need the auto-speak
  // hook so SessionNew responses follow the same loop.
  const autoSpeak = useBrainAutoSpeak({ turns: brain.turns, loading: brain.loading });

  const voice = useVoiceToText({
    onTranscript: (t) => {
      autoSpeak.markVoiceTurn();
      void sendWithFocusCheck(t);
    },
  });

  // If no real conversation yet, show the local intro turn first
  const turns = brain.turns.length > 0 ? brain.turns : [intro];

  // Latest Brain draft payload — feeds the canvas. We pick the most
  // recent assistant turn that has a session-shaped draft attached so
  // the canvas reflects what Brain just proposed without forcing the
  // coach to scroll back through the thread.
  const latestSessionDraft = useMemo(() => {
    for (let i = brain.turns.length - 1; i >= 0; i -= 1) {
      const t = brain.turns[i];
      if (t.draft && typeof t.draft.type === 'string' && t.draft.type.includes('session')) {
        return t.draft.payload;
      }
    }
    return null;
  }, [brain.turns]);

  const handleApprove = useCallback(async (turnId: string, options?: { skipDecisionCheck?: boolean }) => {
    const turn = brain.turns.find((t) => t.id === turnId);
    if (!turn?.draft || !user) return;

    // Decision-making validator (Ben #8) — every TECHNICAL drill in a
    // session plan must declare a decision_under_execution. Skipped on
    // explicit override (coach pressed "Save anyway" after seeing the
    // warning) and on non-session drafts.
    if (!options?.skipDecisionCheck && turn.draft.type.includes('session')) {
      const payload = turn.draft.payload as { drills?: unknown[] } | undefined;
      const drills = Array.isArray(payload?.drills) ? payload.drills : [];
      const forValidation = drills.map((d: unknown) => {
        const dd = (d ?? {}) as Record<string, unknown>;
        return {
          type: (dd.type ?? dd.drill_type ?? 'UNKNOWN') as
            | 'WARM_UP' | 'TECHNICAL' | 'TACTICAL' | 'SMALL_SIDED_GAME' | 'COOL_DOWN',
          decision_under_execution: typeof dd.decision_under_execution === 'string'
            ? dd.decision_under_execution
            : undefined,
        };
      });
      const failures = findTechnicalDrillsMissingDecision(forValidation);
      if (failures.length > 0) {
        setDecisionWarning({ turnId, indices: failures.map((f) => f.index) });
        return;
      }
    }

    setCommitting(turnId);
    setError(null);

    // Offline path — Supabase env is the placeholder, so commitDraft would
    // throw a network error and the coach would lose the draft. Persist to
    // localStorage so SessionDetail / Calendar can pick it up via their
    // local fallbacks, mirroring what onboarding does for Week 1.
    if (isPlaceholderSupabase() && team?.id && turn.draft.type.includes('session')) {
      try {
        const local = saveLocalDraftSession(team.id, turn.draft.payload);
        navigate(`/sessions/${local.id}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not save draft locally.');
        setCommitting(null);
      }
      return;
    }

    try {
      const result = await commitDraft(turn.draft.type, turn.draft.payload, {
        teamId: team?.id ?? null,
        coachId: user.id,
      });
      if (!result.ok) {
        setError(result.error ?? 'Save failed.');
        setCommitting(null);
        return;
      }
      // Brain returns the new session id when type === 'session_plan'
      if (turn.draft.type.includes('session') && result.id) {
        navigate(`/sessions/${result.id}`);
      } else {
        navigate('/sessions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCommitting(null);
    }
  }, [brain.turns, user, team, navigate]);

  // Auto-focus the prompt on mount — coach should be one keystroke (or one
  // mic-tap) away from describing the session.
  useEffect(() => {
    const t = setTimeout(() => {
      const el = document.querySelector<HTMLElement>('.brain-prompt__hint, .brain-prompt__input');
      el?.click?.();
    }, 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <main className="session-new" aria-label="Plan a session">
      <header className="session-new__top">
        <Brand team={team?.name ?? 'Plan tonight'} />
        <GlassPill mono>Layer 2 · session draft</GlassPill>
      </header>

      <section className="session-new__panel">
        <div className="session-new__head">
          <BrainOrb
            size="lg"
            state={voice.listening ? 'listening' : brain.loading ? 'thinking' : 'idle'}
            ariaLabel="Brain"
            className="session-new__orb"
          />
          <span className="session-new__eyebrow">Brain · session planning</span>
          <h1 className="session-new__title">Plan the session.</h1>
          <p className="session-new__sub">
            Talk to Brain or build it yourself — you can take over at any point, and Brain can take over when you want it to.
          </p>
          {level === 'academy' && !canvasMode && (
            <button
              type="button"
              className="session-new__chip"
              onClick={() => setCanvasMode(true)}
              data-testid="switch-to-canvas"
            >
              Switch to canvas
            </button>
          )}
        </div>

        {level === 'academy' && canvasMode && (
          <SessionCanvas
            draftPayload={latestSessionDraft}
            showCanvas
            onSwitchToBrain={() => setCanvasMode(false)}
          />
        )}

        <div className="session-new__pickers" aria-label="Optional session context">
          <div className="session-new__picker-group">
            <span className="session-new__picker-label">Style</span>
            {COACHING_STYLES.map((s) => (
              <button
                key={s}
                type="button"
                className="session-new__chip"
                data-active={style === s || undefined}
                onClick={() => setStyle((prev) => (prev === s ? null : s))}
                title={COACHING_STYLE_META[s].description}
              >
                <span aria-hidden="true">{COACHING_STYLE_META[s].glyph}</span>
                {COACHING_STYLE_META[s].label}
              </button>
            ))}
          </div>
          {tier === 'grassroots' && (
            <div className="session-new__picker-group">
              <span className="session-new__picker-label">Psych-social intent</span>
              {GRASSROOTS_PSYCH_SOCIAL_INTENTS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className="session-new__chip"
                  data-active={intent === id || undefined}
                  onClick={() => setIntent((prev) => (prev === id ? null : id))}
                  title={GRASSROOTS_PSYCH_SOCIAL_INTENT_META[id].description}
                >
                  <span aria-hidden="true">{GRASSROOTS_PSYCH_SOCIAL_INTENT_META[id].glyph}</span>
                  {GRASSROOTS_PSYCH_SOCIAL_INTENT_META[id].label}
                </button>
              ))}
            </div>
          )}
        </div>

        {styleFit && style && (
          <Card variant="opaque" className="session-new__style-warning" data-verdict={styleFit.verdict}>
            <p className="session-new__style-warning-text">
              {styleFit.verdict === 'red'
                ? `${COACHING_STYLE_META[style].label} is a stretch for the squad's likely technical floor — one-touch combinations under pressure tend to frustrate. Try the milder version instead.`
                : `${COACHING_STYLE_META[style].label} is reachable — just default to the milder version and earn the harder progressions late in the session.`}
            </p>
            <ul className="session-new__style-warning-list">
              {styleFit.adjustments.map((adj) => (
                <li key={adj}>{adj}</li>
              ))}
            </ul>
          </Card>
        )}

        <div className="session-new__thread" aria-live="polite">
          {turns.map((turn) => {
            const isBrain = turn.role === 'assistant';
            return (
            <div key={turn.id} className={`session-new__turn session-new__turn--${isBrain ? 'brain' : 'coach'}`}>
              <span className="session-new__turn-speaker">
                {isBrain && <BrainOrb size="xs" ariaLabel="Brain" />}
                {isBrain ? 'Brain' : 'Coach'}
              </span>
              {turn.draft ? (
                <DraftCard
                  type={turn.draft.type}
                  payload={turn.draft.payload}
                  status={committing === turn.id ? 'pending' : turn.status}
                  onApprove={() => void handleApprove(turn.id)}
                  onDismiss={() => brain.dismissDraft(turn.id)}
                />
              ) : (
                <p>{turn.text}</p>
              )}
            </div>
            );
          })}
        </div>

        {focusReport?.message && (
          <Card variant="opaque" className="session-new__focus-warning">
            <p className="session-new__focus-warning-text">💭 {focusReport.message}</p>
            <div className="session-new__focus-warning-actions">
              <Button variant="glass" size="touchline" onClick={() => setFocusReport(null)}>
                Pick a different focus
              </Button>
              <Button variant="primary" size="touchline" onClick={continueAnyway}>
                Continue anyway
              </Button>
            </div>
          </Card>
        )}

        {decisionWarning && (
          <Card variant="opaque" className="session-new__focus-warning" data-verdict="amber">
            <p className="session-new__focus-warning-text">
              🎯 {decisionWarning.indices.length === 1 ? 'One technical drill' : `${decisionWarning.indices.length} technical drills`}
              {' '}haven&apos;t named the decision a player makes during the rep — Ben&apos;s rule:
              technical drills must surface a decision so they aren&apos;t mechanical reps.
              Ask Brain to add &quot;decision under execution&quot; to those drills, or save anyway.
            </p>
            <div className="session-new__focus-warning-actions">
              <Button
                variant="glass"
                size="touchline"
                onClick={() => setDecisionWarning(null)}
              >
                Let me ask Brain
              </Button>
              <Button
                variant="primary"
                size="touchline"
                onClick={() => {
                  const id = decisionWarning.turnId;
                  setDecisionWarning(null);
                  void handleApprove(id, { skipDecisionCheck: true });
                }}
              >
                Save anyway
              </Button>
            </div>
          </Card>
        )}

        <BrainPrompt
          hint={
            focusChecking ? 'Checking recent themes…' :
            brain.loading ? 'Brain is drafting…' :
            'hold the orb or type'
          }
          placeholder="say what comes to mind"
          listening={voice.listening}
          voiceStatus={voice.status}
          onMicStart={() => void voice.start()}
          onMicStop={voice.stop}
          onSubmit={(text) => void sendWithFocusCheck(text)}
        />

        <div className="session-new__row">
          {brain.status && <span className="session-new__status">{brain.status}</span>}
          {error && <Card variant="opaque"><p className="page-error">{error}</p></Card>}
          <div className="session-new__actions">
            <Button variant="glass" size="touchline" onClick={() => navigate('/sessions')}>
              Cancel
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
